import update from 'immutability-helper';
import shoppingCartApi from '../lib/api/shoppingCart';
import shoppingCartService from '../lib/shoppingCart-service';
import {
    selectSeat,
    deselectSeat,
    setSelectedSeatsAndUpdateSelectable,
    selectFreeSeating,
    setSelectedFreeSeatings,
    deselectFreeSeating,
} from './selectedSeatsReducer';
import {identifySelectableSeats} from './selectableSeatsReducer';
import {fetchSeatState} from './seatStateReducer';
import {
    updateAmountOfTickets,
    toggleProcessingTickets,
    setProcessingTickets,
    addShoppingCartMessage,
    deleteAllShoppingCartMessages
} from './eventPageReducer';
import {refreshEvent} from './eventReducer';
import {createOrUpdateTicketAddressData} from './ticketAddressReducer';
import {addMessage} from './seatingplanPageReducer';

import history from '../lib/history';
import {LEO_THEME} from "../dev";
// ------------------------------------
// Constants
// ------------------------------------
export const SET_SHOPPINGCART = 'SHOPPINGCART/SET_SHOPPINGCART';
export const SET_NEW_SHOPPINGCART = 'SHOPPINGCART/SET_NEW_SHOPPINGCART';
export const UNSET_SHOPPINGCART = 'SHOPPIGNCART/UNSET_SHOPPINGCART';
export const SET_SEAT_IN_PROCESS = 'SHOPPINGCART/SET_SEAT_IN_PROCESS';
export const SET_SHIPMENTTYPES = 'SHOPPINGCART/SET_SHIPMENTTYPES';
export const SET_WAITING_IN_QUEUE = 'SHOPPINGCART/WAITING_IN_QUEUE';

export function setShoppingCart(merchantId, shoppingCart) {
    return {
        type: SET_SHOPPINGCART,
        merchantId,
        shoppingCart,
    };
}

export function setNewShoppingCart(merchantId, shoppingCart) {
    return {
        type: SET_NEW_SHOPPINGCART,
        merchantId,
        shoppingCart,
    };
}

export function unsetShoppingCart(merchantId) {
    return {
        type: UNSET_SHOPPINGCART,
        merchantId,
    };
}

export function setSeatInProcess(seatInProcess) {
    return {
        type: SET_SEAT_IN_PROCESS,
        seatInProcess,
    };
}

export function setShipmentTypes(merchantId, shipmentTypes) {
    return {
        type: SET_SHIPMENTTYPES,
        merchantId,
        shipmentTypes,
    };
}

export function setWaitingInQueue(waitingInQueue) {
    return {
        type: SET_WAITING_IN_QUEUE,
        waitingInQueue,
    };
}

// ------------------------------------
// Util Functions
// ------------------------------------
function getIntOrNullFromTheme(module, property) {
    // init tkt selection limit
    const valString = (LEO_THEME.modules[module] && LEO_THEME.modules[module][property])
        ? LEO_THEME.modules[module][property] : '';
    // Validate it's an integer
    let val = null;
    if (/^\d+$/.test(valString)) {
        val = parseInt(valString, 10);
    } else if (valString) {
        console.error('Configured theme value is not an integer!', module, property);
    }
    return val;
}

function getGeneralTktLimitMsg(shoppingCart, event, selAmount) {
    const tktSelectionLimit = getIntOrNullFromTheme('seatingplan', 'tkt-selection-limit');
    const dspEvent = !shoppingCart.events ? null : shoppingCart.events.find(displayEvent => displayEvent.eventUuid === event.uuid);
    const tktAmountCurrentEvent = !dspEvent ? 0 : dspEvent.displayTickets.map(dT => dT.numberOfSeats)
        .reduce((acc, number) => (acc + number), 0);
    if (tktSelectionLimit && ((tktAmountCurrentEvent + selAmount) > tktSelectionLimit)) {
        return {text: 'Sie haben die maximal mögliche Anzahl von Tickets erreicht.', type: 'messages'};
    }
    return null;
}

function getPlLimitMsg(shoppingCart, event, selAmount, priceLevel) {
    const dspEvent = !shoppingCart.events ? null : shoppingCart.events.find(displayEvent => displayEvent.eventUuid === event.uuid);
    const tktAmountOfPl = !dspEvent || !priceLevel ? 0 : dspEvent.displayTickets.filter(dTkt => dTkt.priceLevelUuid === priceLevel.priceLevelUuid)
        .map(dTkt => dTkt.numberOfSeats).reduce((acc, number) => (acc + number), 0);
    if (priceLevel && priceLevel.maxAllowed && tktAmountOfPl >= priceLevel.maxAllowed) {
        return {
            text: `Sie können maximal ${priceLevel.maxAllowed} Ticket(s) für Preisstufe ${priceLevel.name} wählen.`,
            type: 'messages'
        };
    }
    return null;
}

function getTooManyTicketsMsg(shoppingCart, event, selAmount, priceLevel) {
    let msg = getPlLimitMsg(shoppingCart, event, selAmount, priceLevel);
    if (!msg) {
        msg = getGeneralTktLimitMsg(shoppingCart, event, selAmount);
    }
    return msg;
}

function getErrorMsg(error) {
    const msg = error?.response?.message || error?.message || 'Es ist ein Fehler aufgetreten.';
    return {
        text: msg,
        type: 'error',
    }
}

// ------------------------------------
// Action Creator
// ------------------------------------
export function loadShipmentTypesForShoppingCart(secureId, bookingOffice) {
    console.info('LOAD_SHIPMENTTYPES_FOR_SHOPPINGCART');
    return async (dispatch, getState) => {
        const shipmentTypesForShoppingCart = await shoppingCartApi.getShipmentTypesForShoppingCart(
            getState().merchant.merchantId,
            getState().merchant.themeName,
            secureId,
            bookingOffice
        );
        return dispatch(setShipmentTypes(getState().merchant.merchantId, shipmentTypesForShoppingCart));
    };
}

export function loadDisplayShoppingCart(secureId) {
    console.info('LOAD_DISPLAYSHOPPINGCART');
    return async (dispatch, getState) => {
        const displayShoppingCart = await shoppingCartApi.getDisplayShoppingCart(
            getState().merchant.merchantId,
            getState().merchant.themeName,
            secureId
        );
        await dispatch(setShoppingCart(getState().merchant.merchantId, displayShoppingCart));
        dispatch(createOrUpdateTicketAddressData());
        if (getState().bookingOffice) {
            dispatch(loadShipmentTypesForShoppingCart(secureId, getState().bookingOffice.uuid))
        }
        return Promise.resolve()
    };
}

export function loadNewDisplayShoppingCart(secureId) {
    console.info('LOAD_NEW_DISPLAYSHOPPINGCART');
    return async (dispatch, getState) => {
        const displayShoppingCart = await shoppingCartApi.getDisplayShoppingCart(
            getState().merchant.merchantId,
            getState().merchant.themeName,
            secureId
        );
        await dispatch(setNewShoppingCart(getState().merchant.merchantId, displayShoppingCart));
        dispatch(setProcessingTickets(false));
        dispatch(createOrUpdateTicketAddressData());
        if (getState().bookingOffice) {
            dispatch(loadShipmentTypesForShoppingCart(secureId, getState().bookingOffice.uuid));
        }
        return Promise.resolve()
    };
}

export function updateSelectedAndSelectableSeats(eventUuid) {
    return async (dispatch, getState) => {
        const merchantId = getState().merchant.merchantId;
        const shoppingCart = getState().shoppingCart.elements[merchantId];
        const scEvent = (shoppingCart?.events || []).find(event => event.eventUuid === eventUuid);
        const displayTickets = (scEvent || {}).displayTickets;
        if (displayTickets) {
            const seatIdentifiers = displayTickets
                .filter(dt => dt.isVisualTicket)
                .map(seat => seat.seatIdentifier);
            dispatch(setSelectedSeatsAndUpdateSelectable(seatIdentifiers));
            const freeSeatingIdentifiers = displayTickets
                .filter(dt => dt.isVisualBulkTicket)
                .map(seat => seat.seatIdentifier);
            dispatch(setSelectedFreeSeatings(freeSeatingIdentifiers));
        } else {
            dispatch(setSelectedSeatsAndUpdateSelectable([]));
        }
    };
}

export function handleTicketsResponseError(error, messageAction = addShoppingCartMessage) {
    return async (dispatch, getState) => {
        if (error.status === 410) { //error resource gone
            await dispatch(handleShoppingCartError(getState().event.selected));
        } else {
            await dispatch(messageAction(getErrorMsg(error))); //another error --> show message
        }
        return Promise.resolve();
    }
}

export function handleShoppingCartError(eventUuid) {
    return async (dispatch, getState) => {
        await dispatch(unsetShoppingCart(getState().merchant.merchantId));
        if (getState().event?.elements[eventUuid]?.seatingplanUuid) {
            dispatch(setSelectedSeatsAndUpdateSelectable([]));
        }
        history.push(`${eventUuid}/shoppingcart-error`);
        history.go()
        return Promise.resolve();
    };
}

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function waitForShoppingCart() {
    return async (dispatch, getState) => {
        dispatch(setWaitingInQueue(true));
        let shoppingCart;
        let keepTrying = true;
        let backOff = 1; // s

        while (keepTrying) {
            await sleep(backOff * 1000);
            try {
                shoppingCart = await shoppingCartApi.createShoppingCart(
                    getState().merchant.merchantId, getState().merchant.themeName,
                );
                keepTrying = false;
            } catch (e) {
                if (e.status !== 429) {
                    keepTrying = false;
                    dispatch(setWaitingInQueue(false));
                    throw e;
                }
            }
            if (backOff < 16) {
                backOff *= 2;
            }
        }
        dispatch(setWaitingInQueue(false));
        return dispatch(loadNewDisplayShoppingCart(shoppingCart.secureId));
    };
}

export function createShoppingCart() {
    return async (dispatch, getState) => {
        let shoppingCart;
        try {
            shoppingCart = await shoppingCartApi.createShoppingCart(
                getState().merchant.merchantId, getState().merchant.themeName
            );
        } catch (e) {
            if (e.status === 429) { // retry in wait loop if Code == "TOO_MANY_REQUESTS"
                return dispatch(waitForShoppingCart());
            }
            throw e;
        }
        return dispatch(loadNewDisplayShoppingCart(shoppingCart.secureId));
    };
}

export function refreshShoppingCart(merchantId, shoppingCart) {
    return async (dispatch, getState) => {
        let returnedShoppingCart;
        try {
            returnedShoppingCart = await shoppingCartApi.getDisplayShoppingCart(
                merchantId,
                getState().merchant.themeName,
                shoppingCart.secureId
            );
        } catch (e) {
            if (e.status === 410) {
                return dispatch(createShoppingCart());
            }
            throw e;
        }

        await dispatch(setShoppingCart(merchantId, returnedShoppingCart));
        const bookingOffice = getState().bookingOffice;
        if (returnedShoppingCart && bookingOffice) {
            dispatch(createOrUpdateTicketAddressData());
            return dispatch(loadShipmentTypesForShoppingCart(returnedShoppingCart.secureId, bookingOffice.uuid));
        }
        return Promise.resolve();
    };
}

export function fetchShipmentTypesForShoppingCart() {
    return (dispatch, getState) => {
        const merchantId = getState().merchant.merchantId;
        const bookingOffice = getState().bookingOffice;
        if (getState().shoppingCart.elements[merchantId] && bookingOffice) {
            return dispatch(loadShipmentTypesForShoppingCart(getState().shoppingCart.elements[merchantId].secureId, bookingOffice.uuid));
        }
        return Promise.resolve();
    };
}

export function createShoppingCartIfNeeded() {
    return (dispatch, getState) => {
        const merchantId = getState().merchant.merchantId;
        if (!getState().shoppingCart.elements[merchantId]) {
            return dispatch(createShoppingCart());
        }
        return dispatch(refreshShoppingCart(merchantId, getState().shoppingCart.elements[merchantId]));
    };
}

function addTickets(secureId, priceLevelUUID, amount) {
    console.info('ADD_TICKETS');
    return async (dispatch, getState) => {
        try {
            const ticketDto = {
                priceLevelUUID,
                numberOfTickets: amount,
            };
            return await shoppingCartApi.addTickets(
                getState().merchant.merchantId,
                getState().merchant.themeName,
                secureId, getState().event.selected,
                [ticketDto]
            );
        } catch (e) {
            await dispatch(handleTicketsResponseError(e))
        }
    };
}

// TODO: maybe errors could be handled more precise here in future
export function addVisualTicket(seatIdentifier, priceLevelUuid) {
    return async (dispatch, getState) => {
        const shoppingCart = getState().shoppingCart.elements[getState().merchant.merchantId];
        const categoryUUID = getState().seatState.seatInfoMap[seatIdentifier].categoryUUID;
        const event = getState().event.elements[getState().event.selected];
        const cat = event.categories.find(cCat => cCat.categoryUUID === categoryUUID);
        const priceLevel = [cat.standardPriceLevel, ...cat.reducedPriceLevels].find(pl => pl.priceLevelUuid === priceLevelUuid);

        const ticketDto = {
            priceLevelUUID: priceLevelUuid,
            seatIdentifier,
        };

        const tooManyTktsMsg = getTooManyTicketsMsg(shoppingCart, event, 1, priceLevel);
        if (tooManyTktsMsg) {
            return dispatch(addMessage(tooManyTktsMsg));
        }

        dispatch(setSeatInProcess(seatIdentifier));

        try {
            await shoppingCartApi.addVisualTicket(
                getState().merchant.merchantId,
                getState().merchant.themeName,
                shoppingCart.secureId,
                getState().event.selected,
                ticketDto,
            );
            dispatch(selectSeat(seatIdentifier));
            const currentAmountByPriceLevelUuid = getState().eventPage.amountOfTickets[priceLevelUuid] || 0;
            dispatch(updateAmountOfTickets(categoryUUID, priceLevelUuid, (currentAmountByPriceLevelUuid + 1)));
        } catch (e) {
            console.info('seat already reserved');
            await dispatch(handleTicketsResponseError(e, addMessage))
        }

        try {
            await dispatch(identifySelectableSeats(
                getState().selectedSeats.selectedSeats
            ));
            await dispatch(fetchSeatState());
        } catch (e) {
            console.info('coud not identify selectedableSeats', e);
        }
        dispatch(setSeatInProcess(null));
        try {
            return await dispatch(loadDisplayShoppingCart(shoppingCart.secureId));
        } catch {
            return dispatch(handleShoppingCartError(getState().event.selected));
        }
    };
}

export function removeVisualTicket(seatIdentifier) {
    return async (dispatch, getState) => {
        try {
            dispatch(setSeatInProcess(seatIdentifier));
            const eventStorageId = (getState().event.elements[getState().event.selected] || {}).storageId;
            const shoppingCart = getState().shoppingCart.elements[getState().merchant.merchantId];
            const displayTicket = shoppingCartService
                .getDisplayTicketBySeatIdentifier(shoppingCart, seatIdentifier, eventStorageId);
            if (displayTicket) {
                await shoppingCartApi.removeTickets(
                    getState().merchant.merchantId,
                    getState().merchant.themeName,
                    shoppingCart.secureId,
                    displayTicket.ticketItemUuids,
                );
                const currentAmountByPriceLevelUuid = getState().eventPage.amountOfTickets[displayTicket.priceLevelUuid];
                dispatch(updateAmountOfTickets(displayTicket.categoryUuid, displayTicket.priceLevelUuid, (currentAmountByPriceLevelUuid - 1)));
            }
            await dispatch(fetchSeatState());
            dispatch(deselectSeat(seatIdentifier));
            try {
                await dispatch(identifySelectableSeats(
                    getState().selectedSeats.selectedSeats
                ));
            } catch (e) {
                console.info('coud not identify selectedableSeats', e);
            }
            dispatch(setSeatInProcess(null));
            return dispatch(loadDisplayShoppingCart(shoppingCart.secureId));
        } catch {
            return dispatch(handleShoppingCartError(getState().event.selected));
        }
    };
}

export function updatTicketPriceLevels(priceLevelUuid, ticketItemUuids) {
    return async (dispatch, getState) => {
        try {
            const merchantId = getState().merchant.merchantId;
            const shoppingCart = getState().shoppingCart.elements[merchantId];
            const event = getState().event.elements[getState().event.selected];
            const priceLevel = event.categories.flatMap(cat => [cat.standardPriceLevel, ...cat.reducedPriceLevels])
                .find(pl => pl.priceLevelUuid === priceLevelUuid);

            const tooManyTktsMsg = getPlLimitMsg(shoppingCart, event, ticketItemUuids.length, priceLevel);
            if (tooManyTktsMsg) {
                return dispatch(addMessage(tooManyTktsMsg));
            }

            await shoppingCartApi.updateTicketPriceLevels(
                merchantId,
                getState().merchant.themeName,
                shoppingCart.secureId,
                ticketItemUuids,
                priceLevelUuid);

            return dispatch(loadDisplayShoppingCart(shoppingCart.secureId));
        } catch {
            return dispatch(handleShoppingCartError(getState().event.selected));
        }
    };
}

function removeTickets(shoppingCart, priceLevelUuid, amount) {
    console.info('REMOVE_TICKETS');
    return async (dispatch, getState) => {
        try {
            const ticketItemUuidsToDelete = shoppingCartService.getTicketItemUuidsToDelete(
                shoppingCart,
                priceLevelUuid,
                amount
            );
            return await shoppingCartApi.removeTickets(
                getState().merchant.merchantId,
                getState().merchant.themeName,
                shoppingCart.secureId,
                ticketItemUuidsToDelete,
            );
        } catch (e) {
            await dispatch(handleTicketsResponseError(e))
        }
    };
}

export function updateShoppingCart(priceLevelUuid, amount, providedDiff = null) {
    console.info('UPDATE_SHOPPINGCART');
    return async (dispatch, getState) => {
        const state = getState();
        const shoppingCart = state.shoppingCart.elements[state.merchant.merchantId];
        const ticketDifference = providedDiff !== null
            ? providedDiff
            : shoppingCartService.getTicketDifference(
                shoppingCart,
                priceLevelUuid,
                amount
            );

        if (ticketDifference < 0) {
            return await dispatch(removeTickets(shoppingCart, priceLevelUuid, Math.abs(ticketDifference)));
        } else if (ticketDifference > 0) {
            return await dispatch(addTickets(shoppingCart.secureId, priceLevelUuid, ticketDifference));
        }
        return Promise.resolve();
    };
}

export function batchUpdateBulkTickets(selectedTicketsByPriceLevelUuid) {
    console.info('BATCH_UPDATE_BULKTICKETS');
    return async (dispatch, getState) => {
        dispatch(deleteAllShoppingCartMessages());
        dispatch(toggleProcessingTickets());

        const state = getState();
        const merchantId = state.merchant.merchantId;
        const shoppingCart = state.shoppingCart.elements[merchantId];

        const operations = Object.keys(selectedTicketsByPriceLevelUuid).map(uuid => {
            const amount = selectedTicketsByPriceLevelUuid[uuid];
            const diff = shoppingCartService.getTicketDifference(shoppingCart, uuid, amount);
            return { uuid, amount, diff };
        });

        const removals = operations.filter(op => op.diff < 0);
        for (const op of removals) {
            await dispatch(updateShoppingCart(op.uuid, op.amount, op.diff));
        }

        const additions = operations.filter(op => op.diff > 0);
        for (const op of additions) {
            await dispatch(updateShoppingCart(op.uuid, op.amount, op.diff));
        }

        const updatedState = getState();
        const shoppingCartSecureId = updatedState.shoppingCart.elements[merchantId].secureId;
        await dispatch(loadDisplayShoppingCart(shoppingCartSecureId));
        dispatch(refreshEvent());
        dispatch(toggleProcessingTickets());
        const messages = updatedState.eventPage.shoppingCartMessages;
        if (messages.length < 1) {
            const eventUuid = updatedState.event.elements[updatedState.event.selected].uuid;
            const shoppingCartCheckoutUrl = `fs/${eventUuid}/shoppingcart`;
            history.push(shoppingCartCheckoutUrl);
        }
    };
}

export function updateVisualBulkTickets(seatIdentifier, tktsPerPriceLvl) {
    return async (dispatch, getState) => {
        const merchantId = getState().merchant.merchantId;
        const shoppingCart = getState().shoppingCart.elements[merchantId];
        const event = getState().event.elements[getState().event.selected];

        const ticketDto = {
            seatIdentifier,
            tktsPerPriceLvl,
        };

        const dspEvent = !shoppingCart.events ? null : shoppingCart.events.find(displayEvent => displayEvent.eventUuid === event.uuid);
        const currentAmount = !dspEvent ? 0 : dspEvent.displayTickets.filter(dT => dT.seatIdentifier === seatIdentifier).map(dT => dT.numberOfSeats)
            .reduce((acc, number) => (acc + number), 0);
        // Maybe we could pass priceLevelUUID(s) to check with limits per PL
        const amountSelected = Object.values(tktsPerPriceLvl).reduce((acc, val) => (acc + val), 0);
        const tooManyTktsMsg = getTooManyTicketsMsg(shoppingCart, event, amountSelected - currentAmount);
        if (tooManyTktsMsg) {
            return dispatch(addMessage(tooManyTktsMsg));
        }

        dispatch(setSeatInProcess(seatIdentifier));

        try {
            const scResponse = await shoppingCartApi.updateVisualBulkTicket(
                merchantId,
                getState().merchant.themeName,
                shoppingCart.secureId,
                getState().event.selected,
                ticketDto,
            );
            if (scResponse.ticketItems.find(ti => ti.seatIdentifier === seatIdentifier)) {
                dispatch(selectFreeSeating(seatIdentifier));
            } else {
                dispatch(deselectFreeSeating(seatIdentifier));
            }
        } catch (e) {
            dispatch(addMessage(getErrorMsg(e)))
            console.info('Error updating block tickets.');
        }

        try {
            await dispatch(fetchSeatState());
        } catch (e) {
            console.info('coud not load seat state', e);
        }
        dispatch(setSeatInProcess(null));
        try {
            return await dispatch(loadDisplayShoppingCart(shoppingCart.secureId));
        } catch {
            return dispatch(handleShoppingCartError(getState().event.selected));
        }
    };
}

export function removeVisualBulkTickets(seatIdentifier, amount) {
    return async (dispatch, getState) => {
        try {
            dispatch(setSeatInProcess(seatIdentifier));
            const eventStorageId = (getState().event.elements[getState().event.selected] || {}).storageId;
            const merchantId = getState().merchant.merchantId;
            const shoppingCart = getState().shoppingCart.elements[merchantId];
            const displayTickets = shoppingCartService
                .getVisualBulkDisplayTicketsBySeatIdentifier(shoppingCart, seatIdentifier, eventStorageId);

            const ticketItemUuids = displayTickets.flatMap(dt => dt.ticketItemUuids);
            const tktItemUuidsToDelete = ticketItemUuids.slice(0, amount);

            if (tktItemUuidsToDelete && tktItemUuidsToDelete.length > 0) {
                await shoppingCartApi.removeTickets(
                    merchantId,
                    getState().merchant.themeName,
                    shoppingCart.secureId,
                    tktItemUuidsToDelete,
                );
            }
            await dispatch(fetchSeatState());
            if (ticketItemUuids.length === amount) {
                dispatch(deselectFreeSeating(seatIdentifier));
            }
            dispatch(setSeatInProcess(null));
            return dispatch(loadDisplayShoppingCart(shoppingCart.secureId));
        } catch {
            return dispatch(handleShoppingCartError(getState().event.selected));
        }
    };
}

export function removeTicketsByEventUuid(eventUuid) {
    console.info('REMOVE_TICKETS_BY_EVENTUUID');
    return async (dispatch, getState) => {
        const merchantData = getState().merchant;
        const shoppingCart = getState().shoppingCart.elements[merchantData.merchantId];
        try {
            await shoppingCartApi.removeTicketsByEventUuid(
                merchantData.merchantId,
                merchantData.themeName,
                shoppingCart.secureId,
                eventUuid,
            );
            dispatch(refreshEvent())
            return dispatch(loadDisplayShoppingCart(shoppingCart.secureId));
        } catch {
            return dispatch(handleShoppingCartError(getState().event.selected));
        }
    };
}

export function clearShoppingcart() {
    console.info('CLEAR_SHOPPINGCART');
    return async (dispatch, getState) => {
        const merchantData = getState().merchant;
        const shoppingCart = getState().shoppingCart.elements[merchantData.merchantId];
        try {
            await shoppingCartApi.clearShoppingcart(
                merchantData.merchantId,
                merchantData.themeName,
                shoppingCart.secureId,
            );
            return dispatch(loadDisplayShoppingCart(shoppingCart.secureId));
        } catch {
            return dispatch(handleShoppingCartError(getState().event.selected));
        }
    };
}

export function clearShoppingCartIfNecessary(eventUuid) {
    const multiEventBookingValue = (LEO_THEME.modules['shopping-cart'] && LEO_THEME.modules['shopping-cart']['disable-multi-events-booking'])
        ? LEO_THEME.modules['shopping-cart']['disable-multi-events-booking'] : '';
    const disableMultiEventBooking = multiEventBookingValue === 'true';
    return (dispatch, getState) => {
        if (!disableMultiEventBooking) {
            return Promise.resolve();
        }
        const merchantId = getState().merchant.merchantId;
        const shoppingcart = getState().shoppingCart.elements[merchantId];
        if (!shoppingcart) {
            return Promise.resolve();
        }
        const scEvents = shoppingcart.events || [];
        const eventUuids = scEvents.map(scEvent => scEvent.eventUuid);
        if (eventUuids.length > 1 || (eventUuids[0] && eventUuids[0] !== eventUuid)) {
            return dispatch(clearShoppingcart());
        }
        return Promise.resolve();
    };
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
    [SET_SHOPPINGCART]: (state, action) => update(state, {
        elements: {
            [action.merchantId]: {$set: action.shoppingCart},
        },
    }),
    [SET_NEW_SHOPPINGCART]: (state, action) => update(state, {
        elements: {
            [action.merchantId]: {$set: action.shoppingCart},
        },
    }),
    [UNSET_SHOPPINGCART]: (state, action) => update(state, {
        elements: {
            [action.merchantId]: {$set: null},
        },
        seatInProcess: {$set: null},
    }),
    [SET_SEAT_IN_PROCESS]: (state, action) => update(state, {
        seatInProcess: {$set: action.seatInProcess},
    }),
    [SET_SHIPMENTTYPES]: (state, action) => update(state, {
        elements: {
            [action.merchantId]: {
                shipmentTypes: {$set: action.shipmentTypes},
                disabledShipmentInfo: {$set: action.disabledShipmentInfo},
            },
        },
    }),
    [SET_WAITING_IN_QUEUE]: (state, action) => update(state, {
        waitingInQueue: {$set: action.waitingInQueue},
    }),
};

// ------------------------------------
// Reducer
// ------------------------------------
export const initialState = {
    elements: {},
    seatInProcess: null,
    waitingInQueue: false,
};

export default function reducerFunction(state = initialState, action) {
    const handler = ACTION_HANDLERS[action.type];
    return handler ? handler(state, action) : state;
}
